"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Editor } from "@tinymce/tinymce-react";
import MultiSelect from "@/components/ui/multi-select";
import { SITE_DOMAINS } from "@/constants/site-domains";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { TagInput } from "@/components/ui/tag-input";
import { TinyMCEImagePicker } from "@/components/ui/tinymce-image-picker";
import { imageUploadPlugin, sanitizeEditorContent } from "@/components/ui/tinymce-upload-plugin";
import { JsonLdBuilder } from "@/components/ui/json-ld-builder";
import { SeoPreview } from "@/components/ui/seo-preview";
import { useBlogPostStore } from "@/stores/blogPostStore";
import { useAuthorStore } from "@/stores/authorStore";
import { useCategoryStore } from "@/stores/categoryStore";
import { CreateBlogPostDto } from "@/types/blog-post";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ArrowLeft, Save, X, Eye } from "lucide-react";

// Validation schema for blog post
const createBlogPostSchema = z.object({
  title: z
    .string()
    .min(1, "Tiêu đề không được để trống")
    .max(255, "Tiêu đề không được quá 255 ký tự"),
  content: z.string().min(1, "Nội dung không được để trống"),
  slug: z
    .string()
    .min(1, "Đường dẫn không được để trống")
    .max(255, "Đường dẫn không được quá 255 ký tự"),
  author: z.string().min(1, "Tác giả không được để trống"),
  pageTitle: z.string().min(1, "Tiêu đề trang là bắt buộc"),
  pageDescription: z.string().min(1, "Mô tả trang là bắt buộc"),
  canonicalUrl: z.string().min(1, "Canonical URL là bắt buộc"),
  keywords: z.array(z.string()).optional(),
  category: z.array(z.string()).optional(),
  featuredImage: z.string().min(1, "Hình đại diện là bắt buộc"),
  visibility: z.enum(["public", "private"]).optional(),
  publishDate: z.string().optional(),
  updateDate: z.string().optional(),
  isDisplay: z.boolean().optional(),
  // Các trường mới
  mainKeyword: z.string().min(1, "Keyword chính là bắt buộc"),
  keywordUrl: z.string().optional(),
  domains: z.array(z.string()).optional(),
  // Thuộc tính hình ảnh
  imageTitle: z.string().optional(),
  imageAlt: z.string().optional(),
  imageCaption: z.string().optional(),
  imageLinkImage: z.string().optional(),
  imageRel: z.string().optional(),
  imageTarget: z.string().optional(),
  schemaJsonLd: z.string().optional(),
});

type CreateBlogPostFormData = z.infer<typeof createBlogPostSchema>;

export default function CreateBlogPostPage() {
  const navigate = useNavigate();
  const { createBlogPost } = useBlogPostStore();
  const { authors, fetchAuthors } = useAuthorStore();
  const { categories, fetchCategories } = useCategoryStore();
  const editorRef: any = useRef(null);

  // Drag & drop states
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // TinyMCE Image Picker states
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateBlogPostFormData>({
    resolver: zodResolver(createBlogPostSchema),
    defaultValues: {
      title: "",
      content: "",
      slug: "",
      author: "",
      pageTitle: "",
      pageDescription: "",
      canonicalUrl: "",
      keywords: [],
      category: [],
      featuredImage: "",
      mainKeyword: "",
      domains: ["phimmoihd.it.com"],
      visibility: "public",
      isDisplay: true,
      publishDate: "",
      updateDate: "",
      // Image props
      imageTitle: "",
      imageAlt: "",
      imageCaption: "",
      imageLinkImage: "",
      imageRel: "nofollow",
      imageTarget: "_self",
      schemaJsonLd: "",
    },
  });

  // Auto-generate slug from main keyword
  const mainKeywordValue = watch("mainKeyword");
  useEffect(() => {
    const mainKeyword = mainKeywordValue;
    if (mainKeyword && mainKeyword.trim()) {
      const slug = mainKeyword
        .toLowerCase()
        // Convert Vietnamese characters to Latin
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
        .replace(/đ/g, "d") // Convert đ to d
        .replace(/Đ/g, "d") // Convert Đ to d
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single
        .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
        .trim();

      setValue("slug", slug);
    }
  }, [mainKeywordValue, setValue]);

  /**
   * Canonical bám theo đường dẫn: gõ tay hay sinh tự động từ keyword đều cập
   * nhật ngay, không phải chép lại bằng tay.
   *
   * Dựng URL đầy đủ theo domain đang chọn thay vì lưu mỗi slug trần như
   * trước — canonical trần không phải URL hợp lệ, và giờ đã có domain để
   * ghép. Lấy domain đầu tiên: canonical chỉ được có MỘT bản gốc, chọn nhiều
   * site thì site đầu là bản gốc, các site sau trỏ về đó.
   *
   * Người dùng tự sửa canonical thì dừng đồng bộ, không đè lên nữa.
   */
  const slugValue = watch("slug");
  const domainsValue = watch("domains");
  const canonicalTouched = useRef(false);
  useEffect(() => {
    if (canonicalTouched.current) return;
    const domain = domainsValue?.[0] || "phimmoihd.it.com";
    setValue("canonicalUrl", slugValue ? `https://${domain}/tin-tuc/${slugValue}` : "");
  }, [slugValue, domainsValue, setValue]);

  // Fetch authors and categories when component mounts
  useEffect(() => {
    fetchAuthors({ page: 1, limit: 1000 });
    fetchCategories({ page: 1, limit: 1000 });
  }, [fetchAuthors, fetchCategories]);

  // Drag & drop handlers
  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith("image/")) {
      alert("Vui lòng chọn file hình ảnh");
      return;
    }
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
    setValue("featuredImage", file.name);
    // Gợi ý điền sẵn thuộc tính hình ảnh từ tên file
    const baseName = file.name.replace(/\.[^/.]+$/, "");
    setValue("imageTitle", baseName);
    setValue("imageAlt", baseName);
    setValue("imageCaption", "");
    setValue("imageLinkImage", "");
    setValue("imageRel", "nofollow");
    setValue("imageTarget", "_self");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const onSubmit = async (data: CreateBlogPostFormData) => {
    setIsSubmitting(true);
    try {
      const sanitizedContent = await sanitizeEditorContent(data.content);

      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("content", sanitizedContent);
      formData.append("slug", data.slug);
      formData.append("author", data.author);
      if (data.pageTitle) formData.append("pageTitle", data.pageTitle);
      if (data.pageDescription)
        formData.append("pageDescription", data.pageDescription);
      if (data.canonicalUrl) formData.append("canonicalUrl", data.canonicalUrl);
      // FormData không có kiểu mảng — gửi lặp key, Nest gom lại thành string[]
      (data.domains || []).forEach((d) => formData.append("domains[]", d));
      (data.keywords || []).forEach((kw) => formData.append("keywords[]", kw));
      (data.category || []).forEach((cat) =>
        formData.append("category[]", cat)
      );
      if (selectedFile) formData.append("file", selectedFile);
      if (data.visibility) formData.append("visibility", data.visibility);
      if (data.publishDate) formData.append("publishDate", data.publishDate);
      if (data.updateDate) formData.append("updateDate", data.updateDate);
      if (data.mainKeyword) formData.append("mainKeyword", data.mainKeyword);
      if (data.keywordUrl) formData.append("keywordUrl", data.keywordUrl);
      // Image meta
      if (data.imageTitle !== undefined)
        formData.append("imageTitle", data.imageTitle || "");
      if (data.imageAlt !== undefined)
        formData.append("imageAlt", data.imageAlt || "");
      if (data.imageCaption !== undefined)
        formData.append("imageCaption", data.imageCaption || "");
      if (data.imageLinkImage !== undefined)
        formData.append("imageLinkImage", data.imageLinkImage || "");
      if (data.imageRel !== undefined)
        formData.append("imageRel", data.imageRel || "");
      if (data.imageTarget !== undefined)
        formData.append("imageTarget", data.imageTarget || "");
      if (data.schemaJsonLd) formData.append("schemaJsonLd", data.schemaJsonLd);

      await createBlogPost(formData as unknown as CreateBlogPostDto);
      navigate("/blog-posts");
    } catch (error) {
      console.error("Error creating blog post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/blog-posts");
  };

  // TinyMCE Image Picker handlers
  const handleOpenImagePicker = useCallback(() => {
    setIsImagePickerOpen(true);
  }, []);

  const handleImageSelect = useCallback(
    (payload: {
      url: string;
      alt?: string;
      title?: string;
      caption?: string;
      linkImage?: string;
      width?: number;
      height?: number;
      rel?: string;
      target?: string;
      id?: string;
    }) => {
      if (editorRef.current) {
        const imgTag = `<img src="${payload.url}" ${
          payload.id ? `data-image-id="${payload.id}"` : ""
        } alt="${payload.alt || ""}" ${
          payload.width ? `width="${payload.width}"` : ""
        } ${payload.height ? `height="${payload.height}"` : ""} ${
          payload.title ? `title="${payload.title}"` : ""
        } style="max-width: 100%; height: auto;" />`;
        const html = payload.linkImage
          ? `<a href="${payload.linkImage}" ${
              payload.target ? `target="${payload.target}"` : ""
            } ${payload.rel ? `rel="${payload.rel}"` : ""}>${imgTag}</a>`
          : imgTag;
        editorRef.current.insertContent(html);
      }
      setIsImagePickerOpen(false);
    },
    []
  );

  // Preview handler
  const handlePreview = () => {
    // TODO: Implement preview functionality
    console.log("Preview clicked");
  };

  // Memoize Editor to avoid re-render when other fields change
  const contentValue = watch("content");
  const memoizedEditor = useMemo(
    () => (
      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        licenseKey="gpl"
        onInit={(_evt, editor) => (editorRef.current = editor)}
        value={contentValue || ""}
        onEditorChange={(content) => setValue("content", content)}
        init={{
          height: 400,
          menubar: false,
          plugins: [
            "code",
            "advlist",
            "anchor",
            "autolink",
            "charmap",
            "fullscreen",
            "help",
            "image",
            "insertdatetime",
            "link",
            "lists",
            "media",
            "preview",
            "searchreplace",
            "table",
            "visualblocks",
          ],
          toolbar:
            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | customimage",
          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          link_rel_list: [
            { title: "Nothing", value: "" },
            {
              title: "No Referrer",
              value: "noreferrer",
            },
            { title: "No Follow", value: "nofollow" },
            { title: "Sponsored", value: "sponsored" },
            { title: "UGC", value: "ugc" },
          ],
          z_index: 99999,
          z_index_base: 99999,
          dialog_type: "modal",
          body_class: "tinymce-dialog-override",
          dialog_prefix: "tox-",
          // Enable paste and drop for images
          paste_data_images: true,
          automatic_uploads: true,
          file_picker_types: "image",
          setup: (editor) => {
            editor.on("init", () => {
              // Add custom image button
              editor.ui.registry.addButton("customimage", {
                text: "Hình ảnh",
                icon: "image",
                onAction: () => {
                  handleOpenImagePicker();
                },
              });

              // Setup image upload plugin
              imageUploadPlugin(editor);
            });
            editor.on("OpenDialog", (_e) => {
              setTimeout(() => {
                const dialogs = document.querySelectorAll(".tox-dialog");
                dialogs.forEach((dialog) => {
                  (dialog as HTMLElement).style.zIndex = "99999";
                });
              }, 100);
            });
          },
        }}
      />
    ),
    [contentValue, setValue, handleOpenImagePicker]
  );

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-card border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Quay lại
            </Button>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Tạo blog post mới
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePreview}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Xem trước
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Hủy
            </Button>
            <Button
              type="submit"
              form="create-blogpost-form"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4" />
              {isSubmitting ? "Đang lưu..." : "Lưu blog post"}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-12 p-6">
        <form
          id="create-blogpost-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Content Section */}
              <div className="bg-card rounded-lg p-6">
                <Label
                  htmlFor="content"
                  className="text-lg font-semibold mb-3 block"
                >
                  Nội dung
                </Label>
                {memoizedEditor}
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.content.message}
                  </p>
                )}
              </div>

              {/* Author & Category Section */}
              <div className="bg-card rounded-lg p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="author">Tên tác giả</Label>
                    <MultiSelect
                      options={authors.map((a) => ({
                        label: a.name,
                        value: a._id,
                      }))}
                      value={(watch("author") || "")
                        .split(",")
                        .map((s: string) => s.trim())
                        .filter(Boolean)}
                      onChange={(vals) => setValue("author", vals.join(", "))}
                      placeholder="Người viết"
                    />
                    {errors.author && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.author.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="category">Danh mục</Label>
                    <MultiSelect
                      options={categories.map((c) => ({
                        label: c.name,
                        value: c._id,
                      }))}
                      value={watch("category") || []}
                      onChange={(vals) => setValue("category", vals)}
                      placeholder="Chọn danh mục"
                    />
                    {errors.category && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.category.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* SEO Section */}
              <div className="bg-card rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Tối ưu SEO</h3>
                  <button
                    type="button"
                    className="text-blue-600 text-sm hover:underline"
                  >
                    Chỉnh sửa SEO
                  </button>
                </div>

                <div className="mb-4 text-sm text-muted-foreground">
                  Thiết lập các thẻ mô tả giúp khách hàng dễ dàng tìm thấy blog
                  post này trên công cụ tìm kiếm như Google
                </div>

                <div className="mb-4 text-sm text-blue-600">
                  https://example.com/blogs/news/blog-post-mau
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Tiêu đề (Thẻ H1)</Label>
                    <Input
                      id="title"
                      {...register("title")}
                      placeholder="Blog post mẫu"
                    />
                    {errors.title && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="pageTitle">
                        Tiêu đề trang (đề xuất từ 40 - 60 ký tự)
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        {watch("pageTitle")?.length || 0} / 70 ký tự
                      </span>
                    </div>
                    <Input
                      id="pageTitle"
                      {...register("pageTitle")}
                      placeholder="Blog post mẫu"
                      maxLength={70}
                    />
                    {errors.pageTitle && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.pageTitle.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="pageDescription">
                        Mô tả trang (đề xuất từ 50 - 155 ký tự)
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        {watch("pageDescription")?.length || 0} / 320 ký tự
                      </span>
                    </div>
                    <Textarea
                      id="pageDescription"
                      {...register("pageDescription")}
                      placeholder="Mô tả ngắn gọn về blog post cho SEO"
                      maxLength={320}
                      rows={3}
                    />
                    {errors.pageDescription && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.pageDescription.message}
                      </p>
                    )}
                  </div>

                  {/* SEO & URL Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground border-b pb-2">
                      SEO & URL
                    </h3>

                    <div>
                      <Label htmlFor="mainKeyword">Keyword chính</Label>
                      <Input
                        id="mainKeyword"
                        {...register("mainKeyword")}
                        placeholder="Nhập keyword chính..."
                      />
                      {errors.mainKeyword && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.mainKeyword.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label>Hiện trên site</Label>
                      <MultiSelect
                        options={SITE_DOMAINS}
                        placeholder="Chọn site hiển thị"
                        value={watch("domains") || []}
                        onChange={(vals) => setValue("domains", vals)}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Bỏ trống là bài không hiện ở đâu cả. Canonical ghép theo
                        site đầu tiên trong danh sách.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="slug">Đường dẫn</Label>
                      <Input
                        id="slug"
                        {...register("slug")}
                        placeholder="blog-post-mau"
                      />
                      {errors.slug && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.slug.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <Label htmlFor="canonicalUrl">Canonical URL</Label>
                        <span className="text-xs text-muted-foreground">
                          {watch("canonicalUrl")?.length || 0} / 255 ký tự
                        </span>
                      </div>
                      <Input
                        id="canonicalUrl"
                        {...register("canonicalUrl", {
                          onChange: () => {
                            canonicalTouched.current = true;
                          },
                        })}
                        placeholder="https://example.com/blogs/news/blog-post-mau"
                        maxLength={255}
                      />
                      {errors.canonicalUrl && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.canonicalUrl.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="keywords">Keywords</Label>
                      <TagInput
                        value={watch("keywords") || []}
                        onChange={(tags) => setValue("keywords", tags)}
                        placeholder="Nhập từ khóa và nhấn Enter để thêm..."
                        maxTags={10}
                      />
                      {errors.keywords && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.keywords.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="schemaJsonLd">JSON-LD Schema</Label>
                        <JsonLdBuilder
                          formData={watch()}
                          availableFields={[
                            { key: "title", label: "Tiêu đề (H1)" },
                            { key: "pageTitle", label: "Tiêu đề trang (SEO)" },
                            { key: "pageDescription", label: "Mô tả trang" },
                            { key: "canonicalUrl", label: "Canonical URL" },
                            { key: "mainKeyword", label: "Keyword chính" },
                            { key: "slug", label: "Đường dẫn (Slug)" },
                            {
                              key: "keywords",
                              label: "Keywords",
                              getValue: (data) =>
                                Array.isArray(data.keywords)
                                  ? data.keywords.join(", ")
                                  : "",
                            },
                            {
                              key: "authorName",
                              label: "Tên tác giả",
                              getValue: (data) => {
                                const authorId = data.author;
                                const author = authors.find(
                                  (a) => a._id === authorId
                                );
                                return author?.name || "";
                              },
                            },
                            {
                              key: "publishDate",
                              label: "Ngày xuất bản",
                              getValue: (data) =>
                                data.publishDate || new Date().toISOString(),
                            },
                            {
                              key: "updatedDate",
                              label: "Ngày cập nhật",
                              getValue: () => new Date().toISOString(),
                            },
                            {
                              key: "content",
                              label: "Nội dung (Text only)",
                              getValue: (data) => {
                                const div = document.createElement("div");
                                div.innerHTML = data.content || "";
                                return div.textContent || div.innerText || "";
                              },
                            },
                          ]}
                          value={watch("schemaJsonLd") || ""}
                          onChange={(value) => setValue("schemaJsonLd", value)}
                          pageType="article"
                        />
                      </div>
                      <div className="mb-2">
                        <p className="text-xs text-muted-foreground">
                          Nhập JSON-LD structured data cho SEO hoặc dùng nút
                          "Tạo từ Template"
                        </p>
                      </div>
                      <Textarea
                        id="schemaJsonLd"
                        {...register("schemaJsonLd")}
                        placeholder={`{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Tiêu đề bài viết",
  "description": "Mô tả bài viết"
}`}
                        rows={10}
                        className="font-mono text-xs"
                      />
                      {watch("schemaJsonLd") &&
                        (() => {
                          try {
                            JSON.parse(watch("schemaJsonLd")!);
                            return (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ JSON hợp lệ
                              </p>
                            );
                          } catch {
                            return (
                              <p className="text-xs text-red-600 mt-1">
                                ✗ JSON không hợp lệ
                              </p>
                            );
                          }
                        })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* SEO Preview */}
              <SeoPreview
                pageTitle={watch("pageTitle") || watch("title") || ""}
                pageDescription={watch("pageDescription") || ""}
                url={
                  watch("canonicalUrl") ||
                  `https://phimmoihd.it.com/draft/post/${watch("slug") || ""}`
                }
                siteName="GhienPhim"
              />

              {/* Visibility Section */}
              <div className="bg-card rounded-lg p-6">
                <h3 className="font-semibold mb-4">Hiển thị</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="visibility">Chế độ hiển thị</Label>
                    <Select
                      value={watch("visibility") || "public"}
                      onValueChange={(v) => setValue("visibility", v as any)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Chọn chế độ" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Công khai</SelectItem>
                        <SelectItem value="private">Riêng tư</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Date Section */}
              <div className="bg-card rounded-lg p-6">
                <h3 className="font-semibold mb-4">Ngày tháng</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="publishDate">Ngày xuất bản</Label>
                    <DateTimePicker
                      date={
                        watch("publishDate")
                          ? new Date(watch("publishDate")!)
                          : undefined
                      }
                      onChange={(date) =>
                        setValue("publishDate", date?.toISOString() || "")
                      }
                      placeholder="Chọn ngày xuất bản"
                    />
                  </div>

                  <div>
                    <Label htmlFor="updateDate">Ngày cập nhật</Label>
                    <DateTimePicker
                      date={
                        watch("updateDate")
                          ? new Date(watch("updateDate")!)
                          : undefined
                      }
                      onChange={(date) =>
                        setValue("updateDate", date?.toISOString() || "")
                      }
                      placeholder="Chọn ngày cập nhật"
                    />
                  </div>

                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                    <p>
                      <strong>Ngày tạo:</strong>{" "}
                      {new Date().toLocaleString("vi-VN")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Featured Image Section */}
              <div className="bg-card rounded-xl border border-border shadow-sm">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-5 h-5 text-white"
                      >
                        <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75zM6 9.75a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
                        <path d="M3 16.5l4.94-4.94a1.5 1.5 0 012.12 0L15 16.5l2.47-2.47a1.5 1.5 0 012.12 0L21 15.34V17.25A2.25 2.25 0 0118.75 19.5h-13.5A2.25 2.25 0 013 17.25v-.75z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Hình đại diện
                    </h3>
                  </div>

                  {/* Thuộc tính hình ảnh */}
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="imageTitle">Tiêu đề hình ảnh</Label>
                        <Input
                          id="imageTitle"
                          {...register("imageTitle")}
                          placeholder="Tiêu đề hình ảnh"
                        />
                      </div>
                      <div>
                        <Label htmlFor="imageAlt">Alt text</Label>
                        <Input
                          id="imageAlt"
                          {...register("imageAlt")}
                          placeholder="Mô tả hình ảnh"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="imageCaption">Caption</Label>
                      <Textarea
                        id="imageCaption"
                        {...register("imageCaption")}
                        placeholder="Chú thích hình ảnh"
                        rows={2}
                      />
                    </div>
                    {/* Hidden nâng cao để tương thích backend */}
                    <input type="hidden" {...register("imageLinkImage")} />
                    <input type="hidden" {...register("imageRel")} />
                    <input type="hidden" {...register("imageTarget")} />
                  </div>

                  <div
                    ref={dropRef}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer group ${
                      isDragging
                        ? "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 scale-[1.02] shadow-lg"
                        : "border-input hover:border-blue-400 hover:bg-gradient-to-br hover:from-gray-50 hover:to-blue-50 hover:shadow-md"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                      document.getElementById("featured-image-input")?.click()
                    }
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInput}
                      className="hidden"
                      id="featured-image-input"
                    />

                    {previewImage ? (
                      <div className="space-y-4">
                        <div className="relative inline-block">
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="w-48 h-32 rounded-xl object-cover shadow-lg border-4 border-white"
                          />
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-medium text-foreground truncate max-w-xs mx-auto">
                            {selectedFile?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {selectedFile?.size
                              ? (selectedFile.size / 1024 / 1024).toFixed(2)
                              : 0}{" "}
                            MB
                          </p>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage("");
                            setSelectedFile(null);
                            setValue("featuredImage", "");
                          }}
                          className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Xóa ảnh
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center group-hover:from-blue-200 group-hover:to-indigo-200 transition-all duration-300">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-8 h-8 text-blue-600 group-hover:text-blue-700 transition-colors duration-300"
                          >
                            <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75zM6 9.75a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
                            <path d="M3 16.5l4.94-4.94a1.5 1.5 0 012.12 0L15 16.5l2.47-2.47a1.5 1.5 0 012.12 0L21 15.34V17.25A2.25 2.25 0 0118.75 19.5h-13.5A2.25 2.25 0 013 17.25v-.75z" />
                          </svg>
                        </div>

                        <div className="space-y-2">
                          <h4 className="text-lg font-medium text-foreground">
                            Thêm hình đại diện
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Kéo thả hình ảnh vào đây hoặc nhấp để chọn file
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Hỗ trợ: JPG, PNG, GIF (tối đa 10MB)
                          </p>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            document
                              .getElementById("featured-image-input")
                              ?.click();
                          }}
                          className="bg-card border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-800 transition-all duration-200"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Chọn file
                        </Button>
                      </div>
                    )}
                  </div>
                  {errors.featuredImage && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.featuredImage.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* TinyMCE Image Picker */}
        <TinyMCEImagePicker
          isOpen={isImagePickerOpen}
          onClose={() => setIsImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      </div>
    </div>
  );
}
